// PendingInstallments.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation , Link} from 'react-router-dom';
import axios from 'axios';
import {
  Table,
  Typography,
  Spin,
  Alert,
  Button,
  Modal,
  Input,
  Select,
  DatePicker,
  InputNumber
} from 'antd';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UndoOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const { Title, Paragraph } = Typography;
const { Search,TextArea } = Input;

/**
 * PendingInstallments.jsx
 * - Uses same patterns as your FirstInstallments template.
 * - Columns mapped from your DB schema.
 * - Actions included: send remainder (1/2/3), update second installment status/amount/date, change student status, warning message, call status update.
 * - Filters: name, email, source, availability month (extracted where possible), batch day, enrollment type, student status, second installment status, remainder status.
 *
 * Adjust API endpoints if your backend path differs.
 */

function PendingInstallments() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

      const [showFilters, setShowFilters] = useState(false);
  

  const base =
     (role === 'ADMIN') ? "/admin-home"
      : "/user-home";

      const fullPath = `${base}/dashboard-pending-installments`

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Helpers
  const safe = (v) => (v === null || v === undefined || v === '' ? '-' : v);
  const normalize = (v) => {
    if (v === null || v === undefined) return '';
    return String(v).trim().toUpperCase();
  };

  // verify auth on mount / route change
  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );
        if (!res.data?.success) {
          const errMsg = res?.data?.message || 'Please Login Again.';
          toast.error(errMsg);
          localStorage.clear();
          navigate('/login');
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Please Login Again.';
        toast.error(msg);
        localStorage.clear();
        navigate('/login');
      }
    };
    verifyUser();
  }, [navigate, location.pathname, token, email, role]);

  // fetch leads
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/pending-installment/all-pending-installments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res?.data?.success) {
        const data = Array.isArray(res.data.data) ? res.data.data : [];
        setLeads(data.map((it, i) => ({ ...it, key: it.Email || i })));
      } else {
        toast.error('Failed to load pending installments');
      }
    } catch (err) {
      console.error('fetch pending-installments error', err);
      setError(err?.response?.data?.message || 'Error fetching leads');
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [token, location.key, navigate]);

  // --- per-record sending/loading state patterns ---
  const [sendingKeys, setSendingKeys] = useState(new Set());
  const setSending = (k, on = true) =>
    setSendingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(k);
      else next.delete(k);
      return next;
    });


    // Drop modal state (if not already present)
    const [dropModalVisible, setDropModalVisible] = useState(false);
    const [dropComments, setDropComments] = useState('');
    const [dropLoading, setDropLoading] = useState(false);
    
    // --- Warning message modal & sending state ---
// Place these with your other useState declarations.
const [warningModalVisible, setWarningModalVisible] = useState(false);
const [warningRecord, setWarningRecord] = useState(null);
const [warningLoading, setWarningLoading] = useState(false);



const openWarningModal = (record) => {
  setWarningRecord(record);
  setWarningModalVisible(true);
};

const handleSendWarning = async (record) => {
  if (!record?.Email) {
    toast.error('No email present for this lead');
    return;
  }

  const keyName = `warning-${record.key}`;

  // mark sending
  setSendingKeys(prev => {
    const next = new Set(prev);
    next.add(keyName);
    return next;
  });
  setWarningLoading(true);

  try {
    const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/send-warning-message`;
    const payload = {
      Email: record.Email,
      PhoneNumber: record.PhoneNumber
    };

    const res = await axios.put(url, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      timeout: 15000,
    });

    if (res?.data?.success || res?.status === 200 || res?.status === 201) {
      toast.success(res?.data?.message || 'Warning message sent');

      // update UI: set WarningMessage to 'SENT' for that record
      setLeads(prev => prev.map(r => r.key === record.key ? { ...r, WarningMessage: 'SENT' } : r));

      setWarningModalVisible(false);
    } else {
      const err = res?.data?.message || 'Failed to send warning message';
      toast.error(err);
    }
  } catch (err) {
    console.error('send-warning-message error', err);
    const msg = err?.response?.data?.message || err?.message || 'Network/server error';
    toast.error(msg);
  } finally {
    setWarningLoading(false);
    setSendingKeys(prev => {
      const next = new Set(prev);
      next.delete(keyName);
      return next;
    });
  }
};

  

  // CallStatus modal
  const [callModalVisible, setCallModalVisible] = useState(false);
  const [callRecord, setCallRecord] = useState(null);
  const [callValue, setCallValue] = useState(null);
  const [callLoading, setCallLoading] = useState(false);

  const openCallModal = (rec) => {
    setCallRecord(rec);
    setCallValue((rec?.CallStatus ?? '').toString().toUpperCase() || null);
    setCallModalVisible(true);
  };

  const handleUpdateCallStatus = async () => {
    if (!callRecord?.Email || !callValue) return toast.error('Select value');
    const key = `call-${callRecord.key}`;
    setSending(key, true);
    setCallLoading(true);
    try {
      const payload = { Email: callRecord.Email, CallStatus: callValue };
      const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/update-call-status`;
      const res = await axios.put(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res?.data?.success || [200, 201].includes(res?.status)) {
        toast.success(res?.data?.message || 'Call status updated');
        setLeads((prev) => prev.map((r) => (r.key === callRecord.key ? { ...r, CallStatus: callValue } : r)));
        setCallModalVisible(false);
      } else {
        toast.error(res?.data?.message || 'Failed to update');
      }
    } catch (err) {
      console.error('update-call-status', err);
      toast.error(err?.response?.data?.message || err?.message || 'Network error');
    } finally {
      setCallLoading(false);
      setSending(key, false);
    }
  };

  const [commentModalVisible, setCommentModalVisible] = useState(false);
const [commentRecord, setCommentRecord] = useState(null);
const [commentValue, setCommentValue] = useState("");
const [commentLoading, setCommentLoading] = useState(false);

const handleUpdateComment = async () => {
  if (!commentRecord?.Email) {
    return toast.error("No record selected");
  }

  if (!commentValue.trim()) {
    return toast.error("Please enter comment");
  }

  const key = `comment-${commentRecord.key}`;

  setSending(key, true);
  setCommentLoading(true);

  try {
    const payload = {
      Email: commentRecord.Email,
      Comment: commentValue.trim(),
    };

    const url = `${process.env.REACT_APP_API_URL?.replace(
      /\/$/,
      ""
    )}/pending-installment/update-comment`;

    const res = await axios.put(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      timeout: 15000,
    });

    if (res?.data?.success || [200, 201].includes(res?.status)) {
      toast.success(res?.data?.message || "Comment updated");

      setLeads((prev) =>
        prev.map((r) =>
          r.key === commentRecord.key
            ? { ...r, Comment: commentValue.trim() }
            : r
        )
      );

      setCommentModalVisible(false);
      setCommentRecord(null);
      setCommentValue("");
    } else {
      toast.error(res?.data?.message || "Failed to update comment");
    }
  } catch (err) {
    console.error("update-comment", err);
    toast.error(
      err?.response?.data?.message ||
        err?.message ||
        "Network error"
    );
  } finally {
    setCommentLoading(false);
    setSending(key, false);
  }
};
  
  // Student status modal
  const [studentModalVisible, setStudentModalVisible] = useState(false);
  const [studentRecord, setStudentRecord] = useState(null);
  const [studentStatus, setStudentStatus] = useState(null);
  const [studentLoading, setStudentLoading] = useState(false);

  const openStudentModal = (r) => {
    setStudentRecord(r);
    setStudentStatus((r?.StudentStatus ?? '').toString().toUpperCase() || null);
    setStudentModalVisible(true);
  };

  const handleUpdateStudentStatus = async () => {
    if (!studentRecord?.Email || !studentStatus) return toast.error('Select status');
    const key = `student-${studentRecord.key}`;
    setSending(key, true);
    setStudentLoading(true);
    try {
      const payload = { Email: studentRecord.Email, Status: studentStatus };
      const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/update-student-status`;
      const res = await axios.put(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res?.data?.success || [200, 201].includes(res?.status)) {
        toast.success(res?.data?.message || 'Student status updated');
        setLeads((prev) => prev.map((r) => (r.key === studentRecord.key ? { ...r, StudentStatus: studentStatus } : r)));
        setStudentModalVisible(false);
      } else {
        toast.error(res?.data?.message || 'Failed to update');
      }
    } catch (err) {
      console.error('update-student-status', err);
      toast.error(err?.response?.data?.message || err?.message || 'Network error');
    } finally {
      setStudentLoading(false);
      setSending(key, false);
    }
  };

  // ========== Filters ==========
// ---- add these new filter state variables near your other useState filters ----
const [filterFullNameText, setFilterFullNameText] = useState('');
const [filterEmailText, setFilterEmailText] = useState('');
const [filterCategory, setFilterCategory] = useState(null);
const [filterSources, setFilterSources] = useState([]);
const [filterAvailabilityMonth, setFilterAvailabilityMonth] = useState(null);
const [filterBatchDays, setFilterBatchDays] = useState([]);
const [filterCallStatus, setFilterCallStatus] = useState(null);
const [filterEnrollmentType, setFilterEnrollmentType] = useState(null);
const [filterSecondInstallmentStatus, setFilterSecondInstallmentStatus] = useState(null);
const [filterStudentStatus, setFilterStudentStatus] = useState(null);

// NEW: payment remainder (PENDING | SENT) - filters rows if ANY of Remainder_1/2/3 equals this value
const [filterPaymentRemainder, setFilterPaymentRemainder] = useState(null); // 'PENDING' | 'SENT' | null

// NEW: warning message status (PENDING | SENT | other) - matches WarningMessage field
const [filterWarningMessage, setFilterWarningMessage] = useState(null); // 'PENDING' | 'SENT' | null


  // month extractor (attempt from EnrolledBatch or BatchStartDate)
  const MONTHS_FULL = [
    'JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE',
    'JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'
  ];
  const ABBR_TO_FULL = {
    'JAN':'JANUARY','FEB':'FEBRUARY','MAR':'MARCH','APR':'APRIL','MAY':'MAY','JUN':'JUNE',
    'JUL':'JULY','AUG':'AUGUST','SEP':'SEPTEMBER','SEPT':'SEPTEMBER','OCT':'OCTOBER','NOV':'NOVEMBER','DEC':'DECEMBER'
  };

  const monthFromAvailability = (avail, startDate) => {
    if (!avail && !startDate) return '';
    const s = String(avail || startDate || '').toUpperCase().replace(/[.,_/]+/g, ' ').trim();
    for (const m of MONTHS_FULL) if (s.includes(m)) return m;
    const ab = s.match(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|SEPT|OCT|NOV|DEC)\b/);
    if (ab && ABBR_TO_FULL[ab[1]]) return ABBR_TO_FULL[ab[1]];
    const num = s.match(/\b(0?[1-9]|1[0-2])\b/);
    if (num) {
      const n = parseInt(num[1], 10);
      if (n >= 1 && n <= 12) return MONTHS_FULL[n - 1];
    }
    return '';
  };

  const clearFilters = () => {
  setFilterFullNameText('');
  setFilterEmailText('');
  setFilterCategory(null);
  setFilterSources([]);
  setFilterAvailabilityMonth(null);
  setFilterBatchDays([]);
  setFilterCallStatus(null);
  setFilterEnrollmentType(null);
  setFilterSecondInstallmentStatus(null);
  setFilterStudentStatus(null);
  setFilterPaymentRemainder(null);
  setFilterWarningMessage(null);
};

// ---- filteredLeads useMemo ----
const filteredLeads = useMemo(() => {
  if (!Array.isArray(leads)) return [];

  const fname = normalize(filterFullNameText);
  const femail = normalize(filterEmailText);
  const catF = normalize(filterCategory);
  const sourcesF = (filterSources || []).map((s) => normalize(s));
  const batchDaysF = (filterBatchDays || []).map((d) => normalize(d));
  const availMonthF = normalize(filterAvailabilityMonth);
  const callF = normalize(filterCallStatus);
  const enrollTypeF = normalize(filterEnrollmentType);
  const secondInstF = normalize(filterSecondInstallmentStatus);
  const studentF = normalize(filterStudentStatus);
  const paymentRemF = normalize(filterPaymentRemainder);
  const warnF = normalize(filterWarningMessage);

  return leads.filter((r) => {
    // Full name contains
    if (fname) {
      if (!normalize(r.FullName || '').includes(fname)) return false;
    }

    // Email contains
    if (femail) {
      if (!normalize(r.Email || '').includes(femail)) return false;
    }

    // Category exact
    if (catF) {
      if (normalize(r.LeadCategory || '') !== catF) return false;
    }

    // Sources (multi)
    if (sourcesF.length) {
      if (!sourcesF.includes(normalize(r.LeadSource || ''))) return false;
    }

    // Availability month (attempt from EnrolledBatch or BatchStartDate)
    if (availMonthF) {
      const month = monthFromAvailability(r.EnrolledBatch, r.BatchStartDate);
      if (!month) return false;
      if (normalize(month) !== availMonthF) return false;
    }

    // Batch days (multi)
    if (batchDaysF.length) {
      if (!batchDaysF.includes(normalize(r.BatchDay || ''))) return false;
    }

    // Call Status
    if (callF) {
      if (normalize(r.CallStatus || '') !== callF) return false;
    }

    // Enrollment Type
    if (enrollTypeF) {
      if (normalize(r.EnrollmentType || '') !== enrollTypeF) return false;
    }

    
    // Second Installment Status
    if (secondInstF) {
      if (normalize(r.SecondInstallmentStatus || '') !== secondInstF) return false;
    }

    // Student Status
    if (studentF) {
      if (normalize(r.StudentStatus || '') !== studentF) return false;
    }

    // Payment Remainder filter: if selected, require that at least one of Remainder_1/2/3 matches it
    if (paymentRemF) {
      const r1 = normalize(r.Remainder_1 || '');
     
      if (![r1].includes(paymentRemF)) return false;
    }

    // Warning Message filter: check WarningMessage field equals selected value
    if (warnF) {
      if (normalize(r.WarningMessage || '') !== warnF) return false;
    }

    // passed all filters
    return true;
  });
}, [
  leads,
  filterFullNameText,
  filterEmailText,
  filterCategory,
  filterSources,
  filterAvailabilityMonth,
  filterBatchDays,
  filterCallStatus,
  filterEnrollmentType,
  filterSecondInstallmentStatus,
  filterStudentStatus,
  filterPaymentRemainder,
  filterWarningMessage,
]);

  // First Installment Status Modal
  const [installmentModalVisible, setInstallmentModalVisible] = useState(false);
  const [installmentRecord, setInstallmentRecord] = useState(null);
  const [installmentValue, setInstallmentValue] = useState(null);
  const [installmentLoading, setInstallmentLoading] = useState(false);
  
  // Track sending state per record
  const [installmentSendingKeys, setInstallmentSendingKeys] = useState(new Set());
  
  // Open modal handler
  const openInstallmentModal = (record) => {
    setInstallmentRecord(record);
    const cur = (record?.SecondInstallmentStatus ?? '').toString().toUpperCase();
    setInstallmentValue(cur || null);
    setInstallmentModalVisible(true);
  };
  
  const handleChangeInstallmentStatus = async () => {
    if (!installmentRecord || !installmentRecord.Email) {
      toast.error('No record selected');
      return;
    }
    if (!installmentValue) {
      toast.error('Select installment status');
      return;
    }
  
    const keyName = `installment-${installmentRecord.key}`;
    setInstallmentSendingKeys(prev => new Set(prev).add(keyName));
    setInstallmentLoading(true);
  
    try {
      const payload = {
        Email: installmentRecord.Email,
        SecondInstallmentStatus: installmentValue,
      };
  
      const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/update-installment-status`;
      const res = await axios.put(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 15000,
      });
  
      if (res?.data?.success || res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || 'Installment status updated');
  
        // Update UI immediately
        setLeads(prev =>
          prev.map(r =>
            r.key === installmentRecord.key
              ? { ...r, SecondInstallmentStatus: installmentValue }
              : r
          )
        );
  
        setInstallmentModalVisible(false);
      } else {
        toast.error(res?.data?.message || 'Failed to update installment status');
      }
    } catch (err) {
      console.error('update-second-installment-status error', err);
      toast.error(err?.response?.data?.message || err?.message || 'Network/server error');
    } finally {
      setInstallmentLoading(false);
      setInstallmentSendingKeys(prev => {
        const next = new Set(prev);
        next.delete(keyName);
        return next;
      });
    }
  };


  // modal for installment date
  const [installmentDateModalVisible, setInstallmentDateModalVisible] = useState(false);
  const [installmentDateRecord, setInstallmentDateRecord] = useState(null);
  const [installmentDateValue, setInstallmentDateValue] = useState(null); // dayjs object
  const [installmentDateLoading, setInstallmentDateLoading] = useState(false);
  const [installmentDateSendingKeys, setInstallmentDateSendingKeys] = useState(new Set());
  
  
  // --------- Amount Paid (add with other useState declarations) ----------
  const [amountModalVisible, setAmountModalVisible] = useState(false);
  const [amountRecord, setAmountRecord] = useState(null); // record for which modal opened
  const [amountLoading, setAmountLoading] = useState(false);
  const [amountValue, setAmountValue] = useState(null);
  const [amountSendingKeys, setAmountSendingKeys] = useState(new Set());
  
  
  const handleAmountPaid = async (record) => {
    if (!record?.Email) {
      toast.error('Record missing Email');
      return;
    }
    if (!amountValue) {
      toast.error('Please select an amount');
      return;
    }
  
    const keyName = `amount-${record.key}`;
    setAmountSendingKeys(prev => { const next = new Set(prev); next.add(keyName); return next; });
    setAmountLoading(true);
  
    const payload = {
      Email: record.Email,
      AmountPaid: Number(amountValue), // ensure integer
    };
  
    try {
      const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/update-paid-amount`;
      const res = await axios.put(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 15000,
      });
  
      if (res?.data?.success || res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || 'Amount updated');
  
        // update UI row
        setLeads(prev => prev.map(r => r.key === record.key ? { ...r, AmountPaid: payload.AmountPaid } : r));
  
        // close modal
        setAmountModalVisible(false);
        setAmountRecord(null);
        setAmountValue(null);
      } else {
        const err = res?.data?.message || 'Failed to update amount';
        toast.error(err);
      }
    } catch (err) {
      console.error('update-amount error', err);
      const msg = err?.response?.data?.message || err?.message || 'Network/server error';
      toast.error(msg);
    } finally {
      setAmountLoading(false);
      setAmountSendingKeys(prev => {
        const next = new Set(prev);
        next.delete(keyName);
        return next;
      });
    }
  };


  // Inside component function (first installment) - add these new state variables:
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionRecord, setActionRecord] = useState(null);
  const [movingKeys, setMovingKeys] = useState(new Set());

  // helper to set/unset movingKeys
const setMoving = (key, on = true) => {
  setMovingKeys(prev => {
    const next = new Set(prev);
    if (on) next.add(key);
    else next.delete(key);
    return next;
  });
};

// open Action modal
const openActionModal = (record) => {
  setActionRecord(record);
  setActionModalVisible(true);
};

// --- Move to Pending Installments ---
const handleMoveToEnrollmentStage = async (record) => {
  if (!record || !record.Email) return toast.error('Invalid record');
  const key = `move-enrollment-${record.key}`;
  setMoving(key, true);

  try {
    const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/move-to-enrollment-stage`;
    const payload = {
      Email: record.Email
    };

    const res = await axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      timeout: 15000,
    });

    if (res?.data?.success || res?.status === 200 || res?.status === 201) {
      toast.success(res?.data?.message || 'Moved to enrollemnt stage');
      // remove from local list
      setLeads(prev => prev.filter(r => r.key !== record.key));
      setActionModalVisible(false);
    } else {
      toast.error(res?.data?.message || 'Failed to move to enrollemnt stage');
    }
  } catch (err) {
    console.error('move-to-enrollments error', err);
    const msg = err?.response?.data?.message || err?.message || 'Network/server error';
    toast.error(msg);
  } finally {
    setMoving(key, false);
  }
};

const handleDropLeadConfirm = async (record) => {
    if (!record?.Email) {
      toast.error('No email present for selected lead');
      return;
    }

    const key = `drop-lead-${record.key}`;
    setMovingKeys(prev => {
      const next = new Set(prev); next.add(key); return next;
    });
    setDropLoading(true);

    try {
      const payload = { Email: record.Email, Comments: dropComments ?? '' };
      const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/drop-lead`;
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 15000,
      });

      if (res?.data?.success || [200,201].includes(res?.status)) {
        toast.success(res?.data?.message || 'Lead dropped successfully');
        // setLeads(prev => prev.filter(r => r.key !== record.key));
        fetchLeads();
        setDropModalVisible(false);
        setActionModalVisible(false);
      } else {
        const msg = res?.data?.message || 'Failed to drop lead';
        toast.error(msg);
      }
    } catch (err) {
      console.error('Drop lead error', err);
      const msg = err?.response?.data?.message || err.message || 'Network/server error';
      toast.error(msg);
    } finally {
      setDropLoading(false);
      setMovingKeys(prev => {
        const next = new Set(prev); next.delete(key); return next;
      });
    }
  };

  // ========== Table columns ==========
  const columns = [
    {
      title: <span className="text-[17px] font-bold text-white">Full Name</span>,
      dataIndex: 'FullName',
      key: 'FullName',
      width: 220,
      render: (t) => <span className="font-bold whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Email</span>,
      dataIndex: 'Email',
      key: 'Email',
      width: 240,
      render: (t) => <span className="font-bold whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Phone Number</span>,
      dataIndex: 'PhoneNumber',
      key: 'PhoneNumber',
      width: 170,
      render: (t) => <span className="whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Category</span>,
      dataIndex: 'LeadCategory',
      key: 'LeadCategory',
      width: 150,
      render: (t) => <span className="ml-3 font-bold text-amber-700 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Date</span>,
      dataIndex: 'LeadDate',
      key: 'LeadDate',
      width: 120,
      render: (d) => <span className="font-bold whitespace-nowrap">{d ? String(d) : '-'}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Source</span>,
      dataIndex: 'LeadSource',
      key: 'LeadSource',
      width: 190,
      render: (t) => <span className="ml-3 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Enrolled Batch</span>,
      dataIndex: 'EnrolledBatch',
      key: 'EnrolledBatch',
      width: 160,
      render: (t) => <span className="font-bold text-green-700 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Batch Day</span>,
      dataIndex: 'BatchDay',
      key: 'BatchDay',
      width: 120,
      render: (t) => <span className="font-bold text-red-600 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Enrollment Type</span>,
      dataIndex: 'EnrollmentType',
      key: 'EnrollmentType',
      width: 220,
      render: (t) => <span className="font-bold whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Batch Start Date</span>,
      dataIndex: 'BatchStartDate',
      key: 'BatchStartDate',
      width: 190,
      render: (t) => <span className="ml-6 font-bold text-indigo-600 whitespace-nowrap">{t ? String(t) : '-'}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Negotiated Fee</span>,
      dataIndex: 'NegotiatedFee',
      key: 'NegotiatedFee',
      width: 160,
      render: (t) => <span className="ml-6 font-bold text-pink-600 whitespace-nowrap">{t ?? '-'}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">First Installment Date</span>,
      dataIndex: 'FirstInstallmentDate',
      key: 'FirstInstallmentDate',
      width: 230,
      render: (t) => <span className="ml-8 font-bold text-indigo-600 whitespace-nowrap">{t ? String(t) : '-'}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">First Installment Paid Amount</span>,
      dataIndex: 'FirstInstallmentAmount',
      key: 'FirstInstallmentAmount',
      width: 280,
      render: (t) => <span className="ml-10 text-lg font-extrabold text-green-600 whitespace-nowrap">{t ?? '-'}</span>,
    },
    {
      title: <span className="text-[16px] font-bold text-white">Balance To Be Paid</span>,
      dataIndex: 'BalanceInstallmentAmount',
      key: 'BalanceInstallmentAmount',
      width: 180,
      render: (t) => <span className="ml-10 text-lg font-extrabold text-red-600 whitespace-nowrap">{t ?? '-'}</span>,
    },
  {
  title: <span className="text-[17px] font-bold text-white">Remainder_1</span>,
  dataIndex: 'Remainder_1',
  key: 'Remainder_1',
  width: 260,
  align: 'center',
  render: (_, record) => {
    const status = String(record?.Remainder_1 ?? 'PENDING').toUpperCase();
    const isSent = status === 'SENT';

    const keyName = `remainder-1-${record.key}`;
    const isSending = sendingKeys.has(keyName);

    // read remaining ms from state
    const remainingMs = remainderTimers[record.key];

    // determine availability: available to send if remainingMs <= 0 AND not already SENT
    const availableToSend = !isSent && typeof remainingMs === 'number' && remainingMs <= 0;

    // Styled components
    const badgeCommon = 'inline-flex items-center justify-center px-4 py-2 rounded-md font-semibold text-white';
    const sentStyle = { backgroundColor: '#16a34a' }; // green
    const pendingStyle = { backgroundColor: '#dc2626' }; // red
    const availableStyle = { backgroundColor: '#2563eb' }; // blue when button is actionable

    // If already SENT -> green label
    if (isSent) {
      return (
        <div className="flex justify-center">
          <span style={sentStyle} className={`${badgeCommon} min-w-[160px]`}>
            Payment Link Sent
          </span>
        </div>
      );
    }

    // If sending -> show disabled 'Sending...' button
    if (isSending) {
      return (
        <div className="flex justify-center">
          <span style={{ backgroundColor: '#9CA3AF' }} className={`${badgeCommon} min-w-[160px]`}>
            Sending...
          </span>
        </div>
      );
    }

    // If available to send (countdown reached) -> show actionable button
    if (availableToSend) {
      return (
        <div className="flex justify-center">
          <button
            onClick={() => handleSendPaymentRemainder(record)}
            className="px-4 py-2 rounded-md font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-md transition transform active:scale-[0.99]"
            type="button"
          >
            Send Payment Remainder
          </button>
        </div>
      );
    }

    // Otherwise show countdown (red badge) or '-' if target not computable
    if (typeof remainingMs === 'number') {
      return (
        <div className="flex flex-col items-center">
          <span className="mb-1 text-xs text-gray-400">Available in</span>
          <div className="inline-flex items-center justify-center px-3 py-1 rounded-md bg-red-600 text-white font-semibold min-w-[140px]">
            {formatRemaining(remainingMs)}
          </div>
        </div>
      );
    }

    // fallback when BatchStartDate missing / invalid
    return (
      <div className="flex justify-center">
        <span className="inline-flex items-center justify-center px-3 py-1 rounded-md bg-gray-200 text-gray-700 font-semibold min-w-[140px]">
          -
        </span>
      </div>
    );
  },
},

    {
      title: <span className="text-[17px] font-bold text-white">Remainder_2</span>,
      dataIndex: 'Remainder_2',
      key: 'Remainder_2',
      width: 160,
      render: (t) => <span className={`whitespace-nowrap ml-7 font-bold ${String(t).toUpperCase() === 'SENT' ? 'text-green-700' : 'text-red-600'}`}>{safe(t)}</span>,
    },
     {
      title: <span className="text-[17px] font-bold text-white">Remainder_3</span>,
      dataIndex: 'Remainder_3',
      key: 'Remainder_3',
      width: 160,
      render: (t) => <span className={`whitespace-nowrap ml-7 font-bold ${String(t).toUpperCase() === 'SENT' ? 'text-green-700' : 'text-red-600'}`}>{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Call Status</span>,
      dataIndex: 'CallStatus',
      key: 'CallStatus',
      width: 140,
      align: 'center',
      render: (t, record) => {
        const status = String(t ?? 'REQUIRED').toUpperCase();
        const keyName = `call-${record.key}`;
        const isSending = sendingKeys.has(keyName);
        const style = isSending ? { backgroundColor: '#9CA3AF' } : status === 'REQUIRED' ? { backgroundColor: '#dc2626' } : { backgroundColor: '#16a34a' };
        return (
          <div className="flex justify-center">
            <button onClick={() => openCallModal(record)} disabled={isSending} style={{ color: '#fff', ...style, minWidth: 110 }}  className="px-3 py-1.5 rounded-md font-semibold whitespace-nowrap shadow-sm transition-all duration-150">
              {isSending ? <span className="inline-flex items-center gap-2"><Spin size="small" /></span> : status}
            </button>
          </div>
        );
      },
    },
    {
  title: <span className="text-[17px] font-bold text-white">Comment</span>,
  dataIndex: "Comment",
  key: "Comment",
  width: 360,
  align: "center",
  render: (t, record) => {
    const comment = t || "No comment";
    const key = `comment-${record.key}`;
    const isSending = sendingKeys?.has?.(key);

    return (
      <div className="flex flex-col items-center gap-2">
        <span className="max-w-[290px] truncate text-sm font-semibold text-gray-700">
          {comment}
        </span>

        <Button
          size="small"
          type="primary"
          loading={isSending}
          onClick={() => {
            setCommentRecord(record);
            setCommentValue(record?.Comment || "");
            setCommentModalVisible(true);
          }}
          className="!bg-purple-700 hover:!bg-purple-900 !font-bold"
        >
          Update
        </Button>
      </div>
    );
  },
},
    {
  title: <span className="text-[17px] font-bold text-white">Warning Message</span>,
  dataIndex: 'WarningMessage',
  key: 'WarningMessage',
  width: 210,
  align: 'center',
  render: (_, record) => {
    const status = String(record?.WarningMessage ?? 'PENDING').toUpperCase();
    const isSent = status === 'SENT';
    const keyName = `warning-${record.key}`;
    const isSending = sendingKeys.has(keyName);

    const style = isSending
      ? { backgroundColor: '#9CA3AF', borderColor: '#9CA3AF', color: '#fff' }
      : isSent
      ? { backgroundColor: '#16a34a', borderColor: '#16a34a', color: '#fff' } // green
      : { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' }; // red

    return (
      <div className="flex justify-center">
        <button
          onClick={() => openWarningModal(record)}
          disabled={isSending}
          style={{
            ...style,
            minWidth: 140,
           
            cursor: isSending ? 'wait' : 'pointer'
          }}
           className="px-3 py-1.5 rounded-md font-semibold whitespace-nowrap shadow-sm transition-all duration-150"
        >
          {isSending ? (
            <span className="inline-flex items-center gap-2">
              <Spin size="small" />
              Sending...
            </span>
          ) : isSent ? (
            'Warning Sent'
          ) : (
            'Send Warning Message'
          )}
        </button>
      </div>
    );
  },
},

       {
      title: <span className="text-[17px] font-bold text-white">Second Installment Status</span>,
      dataIndex: 'SecondInstallmentStatus',
      key: 'SecondInstallmentStatus',
      width: 260,
      align: 'center',
      render: (t, record) => {
        const status = String(t ?? 'UNPAID').toUpperCase();
        const isUnpaid = status === 'UNPAID' || status === '' || status === 'NULL';
        const isSending = installmentSendingKeys.has(`installment-${record.key}`);
    
        const style = isSending
          ? { backgroundColor: '#9CA3AF', borderColor: '#9CA3AF', color: '#fff' }
          : isUnpaid
          ? { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' } // red
          : { backgroundColor: '#16a34a', borderColor: '#16a34a', color: '#fff' }; // green
    
        const label = isUnpaid ? 'Set Installment Status' : status;
    
        return (
          <div className="flex justify-center">
            <button
              onClick={() => openInstallmentModal(record)}
              disabled={isSending}
              style={{
                ...style,
                minWidth: 90,
               
              }}
             className="px-3 py-1.5 rounded-md font-semibold whitespace-nowrap shadow-sm transition-all duration-150"
              type="button"
            >
              {isSending ? (
                <span className="inline-flex items-center gap-2">
                  <Spin size="small" /> Updating...
                </span>
              ) : (
                label
              )}
            </button>
          </div>
        );
      },
    },
    {
  title: <span className="text-[17px] font-bold text-white">Second Installment Paid Amount</span>,
  dataIndex: 'AmountPaid',
  key: 'AmountPaid',
  width: 300,
  align: 'center',
  render: (t, record) => {
    // local safe parser if you don't have a global parseAmount
    const parseNum = (v) => {
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'number') return isFinite(v) ? v : null;
      const s = String(v).replace(/[₹,\s]/g, '').replace(/[^0-9.\-]/g, '');
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };

    const formatINR = (n) => {
      try {
        return n == null ? '-' : `₹ ${Number(n).toLocaleString('en-IN')}`;
      } catch {
        return String(n);
      }
    };

    const numericVal = parseNum(t);
    const displayVal = numericVal == null ? '-' : formatINR(numericVal);

    const keyName = `amount-${record.key ?? (record.Email ?? record.email ?? Math.random().toString(36).slice(2))}`;
    const isSending = amountSendingKeys && amountSendingKeys.has(keyName);

    const openModalWith = (value) => {
      // prefill modal with numeric value (or null if none)
      // assume setAmountValue expects a number or null
      setAmountRecord(record);
      setAmountValue(value);
      setAmountModalVisible(true);
    };

    if (displayVal === '-') {
      return (
        <div className="flex justify-center">
          <button
            onClick={() => openModalWith(null)}
            disabled={isSending}
            className={`px-4 py-1.5 rounded-md text-white font-semibold transition-all duration-150 ${isSending ? 'bg-gray-400 cursor-wait' : 'bg-red-600 hover:bg-red-700'}`}
          >
            {isSending ? 'Processing...' : 'Update Paid Amount'}
          </button>
        </div>
      );
    }

    // when amount exists: show value + Change button
    return (
      <div className="flex items-center justify-center gap-3">
        <span className="font-bold text-md whitespace-nowrap">{displayVal}</span>

        <button
          onClick={() => openModalWith(numericVal)}
          disabled={isSending}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-150 border ${isSending ? 'bg-gray-200 text-gray-500 border-gray-200 cursor-wait' : 'bg-black text-white border-indigo-200 '}`}
        >
          {isSending ? 'Processing...' : 'Change'}
        </button>
      </div>
    );
  },
},

     {
      title: <span className="text-[17px] font-bold text-white">Second Installment Paid Date</span>,
      dataIndex: 'SecondInstallmentDate',
      key: 'SecondInstallmentDate',
      width: 290,
      align: 'center',
      render: (t, record) => {
        const value = t && t !== '-' ? String(t) : '-';
        const keyName = `installmentDate-${record.key}`;
        const isSending = installmentDateSendingKeys.has(keyName);
    
        if (!value || value === '-') {
          // show button to set date (red)
          return (
            <div className="flex justify-center">
              <button
                onClick={() => {
                  // open modal and preselect record
                  setInstallmentDateRecord(record);
                  setInstallmentDateValue(null);
                  setInstallmentDateModalVisible(true);
                }}
                disabled={isSending}
                style={{
                  backgroundColor: isSending ? '#9CA3AF' : '#dc2626',
                  color: '#fff',
                 
                  minWidth: 190,
                }}
              className="px-3 py-1.5 rounded-md font-bold whitespace-nowrap shadow-sm transition-all duration-150"
                type="button"
              >
                {isSending ? 'Processing...' : 'Set Installment Date'}
              </button>
            </div>
          );
        }
    
        // show formatted date with small "Change" button to edit
        return (
          <div className="flex items-center justify-center gap-3">
            <span className="font-bold text-green-700 whitespace-nowrap">{safe(value)}</span>
            <button
              onClick={() => {
                // try parse existing date (if already in dd-mm-yyyy or ISO)
                let parsed = null;
                try {
                  // accept dd-MM-YYYY or ISO
                  if (/\d{2}-\d{2}-\d{4}/.test(value)) {
                    parsed = dayjs(value, 'DD-MM-YYYY');
                  } else {
                    parsed = dayjs(value);
                  }
                  if (!parsed || !parsed.isValid()) parsed = null;
                } catch (e) {
                  parsed = null;
                }
                setInstallmentDateRecord(record);
                setInstallmentDateValue(parsed);
                setInstallmentDateModalVisible(true);
              }}
              className="px-3 py-1 font-medium text-gray-900 border-2 border-black rounded-md bg-amber-400"
              type="button"
            >
              Change
            </button>
          </div>
        );
      },
    },
    {
      title: <span className="text-[17px] font-bold text-white">Student Status</span>,
      dataIndex: 'StudentStatus',
      key: 'StudentStatus',
      width: 150,
      align: 'center',
      render: (t, record) => {
        const status = String(t ?? 'ACTIVE').toUpperCase();
        const keyName = `student-${record.key}`;
        const isSending = sendingKeys.has(keyName);
        const style = isSending ? { backgroundColor: '#9CA3AF' } : status === 'ACTIVE' ? { backgroundColor: '#16a34a' } : { backgroundColor: '#dc2626' };
        return (
          <div className="flex justify-center">
            <button onClick={() => openStudentModal(record)} disabled={isSending} style={{ color: '#fff', ...style, minWidth: 110 }} className="px-3 py-1.5 rounded-md font-semibold">
              {isSending ? <Spin size="small" /> : status}
            </button>
          </div>
        );
      },
    },
    {
      title: (
        <span className="font-bold text-[17px] text-white whitespace-nowrap">
          Action
        </span>
      ),
      key: 'action',
      fixed: 'right',
      width: 200,
      align: 'center',
      render: (_, record) => {
        return (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => openActionModal(record)}
              className={`px-4 py-2 rounded-md text-white font-semibold transition-colors duration-200 whitespace-nowrap bg-gradient-to-r from-blue-600 to-blue-500 hover:from-red-700 hover:to-rose-600 shadow`}
              type="button"
            >
              Actions
            </button>
          </div>
        );
      },
    }
  ];


// ---------- Robust remainder helpers & effect ----------

// helper to try parse many common date formats into a Date object
const parseBatchStartDate = (value) => {
  if (!value && value !== 0) return null;

  // if already a Date
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const s = String(value).trim();

  // 1) Try native Date parse (ISO or timestamp)
  const d0 = new Date(s);
  if (!Number.isNaN(d0.getTime())) return d0;

  // 2) Try patterns: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, YYYY/MM/DD
  const isoLike = /^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/;
  const dmy = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/;
  let m;
  if ((m = s.match(isoLike))) {
    const [_, y, mo, da] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(da));
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  if ((m = s.match(dmy))) {
    const [_, da, mo, y] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(da));
    if (!Number.isNaN(dt.getTime())) return dt;
  }

  // 3) Try formats like "26-OCT-2025" or "26-OCTOBER-2025" (case-insensitive)
  const monthMap = {
    JAN: 0, JANUARY: 0, FEB: 1, FEBRUARY: 1, MAR: 2, MARCH: 2, APR: 3, APRIL: 3,
    MAY: 4, JUN: 5, JUNE: 5, JUL: 6, JULY: 6, AUG: 7, AUGUST: 7, SEP: 8, SEPTEMBER: 8,
    OCT: 9, OCTOBER: 9, NOV: 10, NOVEMBER: 10, DEC: 11, DECEMBER: 11
  };

  // split on non-alphanumeric (hyphen, space, slash, comma)
  const parts = s.split(/[\s,\/\-]+/).map(p => p.trim()).filter(Boolean);

  // case: [DD, MONTHNAME, YYYY] or [DD, MON, YYYY]
  if (parts.length >= 3) {
    const maybeDay = parts[0];
    const maybeMonth = parts[1].toUpperCase();
    const maybeYear = parts[2];

    if (/^\d{1,2}$/.test(maybeDay) && /^\d{4}$/.test(maybeYear) && monthMap.hasOwnProperty(maybeMonth)) {
      const y = Number(maybeYear);
      const mo = monthMap[maybeMonth];
      const da = Number(maybeDay);
      const dt = new Date(y, mo, da);
      if (!Number.isNaN(dt.getTime())) return dt;
    }
  }

  // 4) Try to find any token that looks like a month name and a nearby year+day
  // fallback: try to extract day, month token, year from anywhere
  const tokens = s.split(/[^A-Za-z0-9]+/).filter(Boolean);
  let found = null;
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i].toUpperCase();
    if (monthMap.hasOwnProperty(tok)) {
      // try neighbors
      const prev = tokens[i - 1];
      const next = tokens[i + 1];
      const dayCandidate = prev && /^\d{1,2}$/.test(prev) ? Number(prev) : null;
      const yearCandidate = next && /^\d{4}$/.test(next) ? Number(next) : null;
      if (dayCandidate && yearCandidate) {
        const dt = new Date(yearCandidate, monthMap[tok], dayCandidate);
        if (!Number.isNaN(dt.getTime())) return dt;
      }
    }
  }

  // give up
  return null;
};

// compute target Date = BatchStartDate + 14 days at 17:00 local
const getRemainderTarget = (record) => {
  const raw = record?.BatchStartDate ?? record?.BatchStartDate; // adapt if different property name
  const parsed = parseBatchStartDate(raw);
  if (!parsed) return null;
  const target = new Date(parsed.getTime() + 25 * 24 * 60 * 60 * 1000);
  target.setHours(14, 0, 0, 0); // 14:00 local
  return target;
};

// ms -> Dd HH:MM:SS or HH:MM:SS
const formatRemaining = (ms) => {
  if (ms == null) return '-';
  if (ms <= 0) return '00:00:00';
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(mins).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  if (days > 0) return `${days}d ${hh}:${mm}:${ss}`;
  return `${hh}:${mm}:${ss}`;
}; 

// state for per-row ms remaining
const [remainderTimers, setRemainderTimers] = useState({});

// start the ticker and compute initial values whenever leads change
useEffect(() => {
  if (!Array.isArray(leads)) {
    setRemainderTimers({});
    return;
  }

  // initial compute
  const initial = {};
  leads.forEach((r) => {
    const target = getRemainderTarget(r);
    initial[r.key] = target ? Math.max(0, target.getTime() - Date.now()) : null;
  });
  setRemainderTimers(initial);

  // interval tick every 1s
  const id = setInterval(() => {
    setRemainderTimers((prev) => {
      const next = { ...prev };
      let changed = false;
      leads.forEach((r) => {
        const key = r.key;
        const target = getRemainderTarget(r);
        const newVal = target ? Math.max(0, target.getTime() - Date.now()) : null;
        // Only set if changed (avoids unnecessary re-renders)
        if (prev[key] !== newVal) {
          next[key] = newVal;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, 1000);

  return () => clearInterval(id);
}, [leads]);


const handleSendPaymentRemainder = async (record) => {
  if (!record?.Email) {
    toast.error('No Email found on this record');
    return;
  }

  const keyName = `remainder-1-${record.key}`;
  // mark sending
  setSendingKeys(prev => {
    const next = new Set(prev);
    next.add(keyName);
    return next;
  });

  try {
    const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/send-payment-link`;
    const payload = { Email: record.Email };

    const res = await axios.put(url, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      timeout: 15000,
    });

    if (res?.data?.success || res?.status === 200 || res?.status === 201) {
      toast.success(res?.data?.message || 'Payment link sent');

      // update local UI: mark Remainder_1 = 'SENT' (or use field name you store)
      setLeads(prev => prev.map(r => (r.key === record.key ? { ...r, Remainder_1: 'SENT' } : r)));

      // Optionally store returned payment link into PaymentLink_1 if API returns it:
      // setLeads(prev => prev.map(r => r.key === record.key ? { ...r, PaymentLink_1: res?.data?.paymentLink || r.PaymentLink_1 } : r));
    } else {
      const errMsg = res?.data?.message || 'Failed to send payment reminder';
      toast.error(errMsg);
    }
  } catch (err) {
    console.error('send-payment-link error', err);
    const msg = err?.response?.data?.message || err?.message || 'Network / server error';
    toast.error(msg);
  } finally {
    // unmark sending
    setSendingKeys(prev => {
      const next = new Set(prev);
      next.delete(keyName);
      return next;
    });
  }
};

let juniorFilter = filteredLeads.filter(r => {
    return r.EnrollmentType === 'JUNIOR PENETRATION TESTER'
  })

  let seniorFilter = filteredLeads.filter(r => {
    return r.EnrollmentType === 'SENIOR PENETRATION TESTER'
  })

  let studentFilter = filteredLeads.filter(r => {
    return r.LeadCategory === 'STUDENT'
  })

  let professionalFilter = filteredLeads.filter(r => {
    return r.LeadCategory === 'PROFESSIONAL'
  })

  // ========== Render UI ==========
  return (
    <div className="w-full px-6 py-8 mx-auto bg-white shadow-sm rounded-xl">
       <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              className="!mb-6 !ml-4"
              toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
            />

      <Title level={3} className="!text-3xl !font-bold text-gray-800 tracking-tight mb-3 border-b border-gray-200 pb-2">
        Pending Installments
      </Title>

      <Paragraph className="mb-6 text-base leading-relaxed text-gray-700">
        Leads with pending installments. Use the controls to send reminders, update installment status, or change student status.
      </Paragraph>

       <p className="text-lg text-slate-700">
                    You can access all lead details anytime from the{" "}
                    <Link
                      to={fullPath}
                      className="inline-flex items-center gap-2 px-3 py-1 text-lg font-semibold text-red-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Pending Installment Dashboard
                    </Link>
                  </p>

      {error && (
        <Alert type="error" message="Error" description={error} showIcon closable className="mb-6" />
      )}

 <div className="w-full mt-6">
         {/* FILTER TOGGLE BUTTON */}
               <div className="flex justify-end mb-3">
                 <Button
                   onClick={() => setShowFilters(!showFilters)}
                   type="primary"
                   className="
                     font-bold
                     px-5
                     rounded-lg
                     bg-gradient-to-r
                     from-blue-700
                     via-indigo-700
                     to-purple-700
                     border-none
                     text-white
                     shadow-[0_0_20px_rgba(79,70,229,0.8)]
                     hover:shadow-[0_0_35px_rgba(79,70,229,1)]
                     hover:scale-105
                     animate-pulse
                     transition-all
                     duration-300
                   "
                 >
                   {showFilters ? "Hide Filters" : "Show Filters"}
                 </Button>
               </div>
               
               {/* Filters with transition */}
               <div
                 className={`overflow-hidden transition-all duration-500 ease-in-out ${
                   showFilters
                     ? "max-h-[900px] opacity-100 translate-y-0 mb-3"
                     : "max-h-0 opacity-0 -translate-y-3 mb-0"
                 }`}
               >
               <div className="p-5 border border-slate-400 shadow-[0_10px_35px_rgba(15,23,42,0.12)] bg-gradient-to-br from-white via-slate-50 to-blue-50 rounded-2xl">
               
               
         <div className="flex items-center gap-3 my-6 ml-4">
    {/* FullName Search */}
    <div className="flex items-center gap-2">
      <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Full Name</label>
      <Search
        placeholder="Search Full Name"
        allowClear
        value={filterFullNameText}
        onChange={(e) => setFilterFullNameText(e.target.value)}
        onSearch={(v) => setFilterFullNameText(v)}
        style={{ minWidth: 260, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
        enterButton={false}
      />
    </div>

    {/* Email Search */}
    <div className="flex items-center gap-2">
      <label className="text-sm font-semibold text-gray-700">Email</label>
      <Search
        placeholder="Search email"
        allowClear
        value={filterEmailText}
        onChange={(e) => setFilterEmailText(e.target.value)}
        onSearch={(v) => setFilterEmailText(v)}
        style={{ minWidth: 260, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
        enterButton={false}
      />
    </div>

    <div className="flex-grow ml-4 border-2 border-t border-gray-400"></div>
  </div>

        {/* Filters toolbar */}
          <div className="flex flex-col gap-4 mb-7 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-3">
        
              {/* Lead Category */}
              <div className="flex items-center gap-2 ml-4">
                <label className="text-sm font-semibold text-gray-700">Lead Category</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>All</span>}
                  value={filterCategory}
                  onChange={(v) => setFilterCategory(v || null)}
                  allowClear
                  style={{ minWidth: 160, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'Student', value: 'STUDENT' }, { label: 'Professional', value: 'PROFESSIONAL' } ]}
                />
              </div>
        
              {/* Lead Source (multi) */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Lead Source</label>
                <Select
                  mode="multiple"
                  placeholder={<span style={{ color: '#4b5563' }}>Select Source</span>}
                  value={filterSources}
                  onChange={(v) => setFilterSources(v || [])}
                  allowClear
                  style={{ minWidth: 220, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'Google', value: 'GOOGLE' }, 
                    { label: 'Meta', value: 'META' },
                     { label: 'Meta-Instagram', value: 'META-INSTAGRAM' },
             { label: 'Meta-Facebook', value: 'META-FACEBOOK' },
                     { label: 'LinkedIn', value: 'LINKEDIN' },
                      { label: 'Organic', value: 'ORGANIC' },
                       { label: 'Youtube', value: 'YOUTUBE' },
                        { label: 'Youtube-Infeed', value: 'YOUTUBE-YIF' },
                       { label: 'Youtube-Instream', value: 'YOUTUBE-YIS' },
                       ]}
                />
              </div>
        
              {/* Availability Month */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Availability Month</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Select Month</span>}
                  value={filterAvailabilityMonth}
                  onChange={(v) => setFilterAvailabilityMonth(v || null)}
                  allowClear
                  style={{ minWidth: 140, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'JANUARY', value: 'JANUARY' }, { label: 'FEBRUARY', value: 'FEBRUARY' }, { label: 'MARCH', value: 'MARCH' },
                    { label: 'APRIL', value: 'APRIL' }, { label: 'MAY', value: 'MAY' }, { label: 'JUNE', value: 'JUNE' },
                    { label: 'JULY', value: 'JULY' }, { label: 'AUGUST', value: 'AUGUST' }, { label: 'SEPTEMBER', value: 'SEPTEMBER' },
                    { label: 'OCTOBER', value: 'OCTOBER' }, { label: 'NOVEMBER', value: 'NOVEMBER' }, { label: 'DECEMBER', value: 'DECEMBER' },
                  ]}
                />
              </div>
        
              {/* Batch Day (multi) */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Batch Day</label>
                <Select
                  mode="multiple"
                  placeholder={<span style={{ color: '#4b5563' }}>Select Batch Day</span>}
                  value={filterBatchDays}
                  onChange={(v) => setFilterBatchDays(v || [])}
                  allowClear
                  style={{ minWidth: 160, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'Morning', value: 'MORNING' }, { label: 'Evening', value: 'EVENING' } ]}
                />
              </div>
        
              
        
              {/* Enrollment Type */}
            <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Enrollment Type</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                  value={filterEnrollmentType}
                  onChange={(v) => setFilterEnrollmentType(v || null)}
                  allowClear
                  style={{ minWidth: 220, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'JUNIOR PENETRATION TESTER', value: 'JUNIOR PENETRATION TESTER' }, { label: 'SENIOR PENETRATION TESTER', value: 'SENIOR PENETRATION TESTER' } ]}
                />
            </div>
        
             

              {/* Payment Remainder */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Payment Remainder</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                  value={filterPaymentRemainder}
                  onChange={(v) => setFilterPaymentRemainder(v || null)}
                  allowClear
                  style={{ minWidth: 120, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'PENDING', value: 'PENDING' }, { label: 'SENT', value: 'SENT' } ]}
                />
              </div>

              {/* Call Status */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Call Status</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                  value={filterCallStatus}
                  onChange={(v) => setFilterCallStatus(v || null)}
                  allowClear
                  style={{ minWidth: 140, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'REQUIRED', value: 'REQUIRED' }, { label: 'NOT REQUIRED', value: 'NOT REQUIRED' }, { label: 'PERFORMED', value: 'PERFORMED' } ]}
                />
              </div>
        
         {/* Warning Message */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Warning Message</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                  value={filterWarningMessage}
                  onChange={(v) => setFilterWarningMessage(v || null)}
                  allowClear
                  style={{ minWidth: 120, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'PENDING', value: 'PENDING' }, { label: 'SENT', value: 'SENT' } ]}
                />
              </div>
              
        
              {/* Second Installment Status */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Second Installment Status</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                  value={filterSecondInstallmentStatus}
                  onChange={(v) => setFilterSecondInstallmentStatus(v || null)}
                  allowClear
                  style={{ minWidth: 160, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'UNPAID', value: 'UNPAID' }, { label: 'PAID', value: 'PAID' } ]}
                />
              </div>
 
               {/* Call Status */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Student Status</label>
                <Select
                  placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                  value={filterStudentStatus}
                  onChange={(v) => setFilterStudentStatus(v || null)}
                  allowClear
                  style={{ minWidth: 140, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[ { label: 'ACTIVE', value: 'ACTIVE' }, { label: 'INACTIVE', value: 'INACTIVE' } ]}
                />
              </div>
        
             
        
              {/* Clear Filters button */}
              <div className="ml-4">
                <Button onClick={clearFilters} icon={<UndoOutlined />} type="default" className="font-semibold text-black bg-blue-400 border border-gray-400 hover:bg-gray-50">
                  Clear Filters
                </Button>
              </div>
        
            </div>
          </div>
      </div>
      </div>

       <div className="flex items-center gap-4 mb-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Total Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {filteredLeads.length}
    </div>
  </div> 

  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Student Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {studentFilter.length}
    </div>
  </div> 

<div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Professional Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {professionalFilter.length}
    </div>
  </div> 
  
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Junior
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {juniorFilter.length}
    </div>
  </div> 

  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Senior
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {seniorFilter.length}
    </div>
  </div> 
  
</div>

      <Table
        dataSource={filteredLeads}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 25, total: filteredLeads.length }}
        scroll={{ x: 3500 }}
         className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table-pending"
      />
</div>


 {/* Action Modal */}
                  <Modal
                    title={<div className="flex items-center justify-center w-full"><span className="text-xl font-bold text-gray-900">Choose action for lead</span></div>}
                    open={actionModalVisible}
                    onCancel={() => setActionModalVisible(false)}
                    footer={null}
                    centered
                    width={520}
                    bodyStyle={{ padding: 24 }}
                  >
                    <div className="space-y-4">
                      <div className="text-sm text-gray-700">
                        Performing action for: <strong>{actionRecord?.FullName ?? actionRecord?.Email ?? '-'}</strong>
                        {actionRecord?.PhoneNumber ? <span className="ml-2 text-xs font-semibold text-gray-700">({actionRecord.PhoneNumber})</span> : null}
                      </div>
            
                      <div className="flex flex-col gap-3">
                        <button onClick={() => handleMoveToEnrollmentStage(actionRecord)} disabled={!actionRecord || movingKeys.has(`move-enrollment-${actionRecord?.key}`)} className={`w-full px-5 py-3 rounded-xl text-white font-semibold shadow-md transition-transform active:scale-[0.99] ${movingKeys.has(`move-enrollment-${actionRecord?.key}`) ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'}`} type="button">
                          {movingKeys.has(`move-enrollment-${actionRecord?.key}`) ? <span className="inline-flex items-center gap-2"><Spin size="small" />Moving...</span> : 'Move To Enrollment Stage'}
                        </button>
                        <button onClick={() => { setDropComments(''); setDropModalVisible(true); }} disabled={!actionRecord || movingKeys.has(`drop-lead-${actionRecord?.key}`)} className={`w-full px-5 py-3 rounded-xl text-white font-semibold shadow-md transition-transform active:scale-[0.99] ${movingKeys.has(`drop-lead-${actionRecord?.key}`) ? 'bg-gray-400 cursor-wait' : 'bg-red-600 hover:bg-red-700'}`} type="button">
                          Drop The Lead
                        </button>
                      </div>
                    </div>
                  </Modal>

                   {/* Drop Modal */}
                                    <Modal
                                      title={<span className="text-lg font-bold">Drop Lead</span>}
                                      visible={dropModalVisible}
                                      onCancel={() => setDropModalVisible(false)}
                                      footer={null}
                                      centered
                                      width={560}
                                    >
                                      <div className="space-y-4">
                                        <div className="text-sm text-gray-700">Dropping lead: <strong>{actionRecord?.FullName ?? actionRecord?.Email ?? '-'}</strong></div>
                              
                                        <div>
                                          <label className="block mb-1 text-sm text-gray-700">Email</label>
                                          <input type="text" value={actionRecord?.Email ?? ''} readOnly className="w-full px-3 py-2 text-sm font-semibold bg-gray-100 border border-gray-200 rounded-md" />
                                        </div>
                              
                                        <div>
                                          <label className="block mb-1 text-sm text-gray-700">Comments</label>
                                          <TextArea value={dropComments} onChange={(e) => setDropComments(e.target.value)} rows={4} placeholder="Reason / notes for dropping this lead..." />
                                        </div>
                              
                                        <div className="flex items-center justify-end gap-3">
                                          <button onClick={() => setDropModalVisible(false)} className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-md" type="button" disabled={dropLoading}>Cancel</button>
                                          <button onClick={() => handleDropLeadConfirm(actionRecord)} className={`px-5 py-2.5 rounded-md text-white font-semibold ${dropLoading ? 'bg-gray-400 cursor-wait' : 'bg-red-600 hover:bg-red-700'}`} type="button" disabled={dropLoading}>
                                            {dropLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" /> Dropping...</span> : 'Drop Lead'}
                                          </button>
                                        </div>
                                      </div>
                                    </Modal>
    
      {/* Call Status Modal */}
      <Modal title={<span className="text-lg font-bold">Change Call Status</span>}
        open={callModalVisible}
        onCancel={() => setCallModalVisible(false)}
        footer={null}
        centered
        width={520}
      >
        <div className="space-y-4">
          <div>Change call status for <strong>{callRecord?.FullName ?? '-'}</strong></div>
          <div>
            <label className="block mb-1 text-sm text-gray-700">Email</label>
            <input readOnly value={callRecord?.Email ?? ''} className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md" />
          </div>
          <div>
            <label className="block mb-1 text-sm text-gray-700">Call Status</label>
            <Select value={callValue} onChange={(v) => setCallValue(v)} options={[
              { label: 'PERFORMED', value: 'PERFORMED' }, { label: 'NOT REQUIRED', value: 'NOT REQUIRED' }, { label: 'REQUIRED', value: 'REQUIRED' }
            ]} style={{ width: '100%' }} />
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button onClick={() => setCallModalVisible(false)} disabled={callLoading}>Cancel</Button>
            <Button type="primary" onClick={handleUpdateCallStatus} disabled={callLoading || !callValue} className="bg-blue-600">
              {callLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" />Updating...</span> : 'Update Call Status'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title={<span className="text-lg font-bold">Change Second Installment Status</span>}
        visible={installmentModalVisible}
        onCancel={() => setInstallmentModalVisible(false)}
        footer={null}
        centered
        width={520}
      >
        <div className="space-y-4">
           <div className="text-sm text-gray-700">
            Change Second Installment Status to : <strong className="text-sm text-red-700">{installmentRecord?.FullName ?? '-'}</strong>
          </div>
          <div>
            <label className="block mb-1 text-gray-600 text-md">Email</label>
            <input
              type="text"
              value={installmentRecord?.Email ?? ''}
              readOnly
              className="w-full px-3 py-2 text-sm font-bold bg-gray-100 border border-gray-200 rounded-md"
            />
          </div>
      
          <div>
            <label className="block mb-1 text-gray-600 text-md">Second Installment Status</label>
            <Select
              value={installmentValue}
              onChange={(v) => setInstallmentValue(v)}
              placeholder="Select status"
              style={{ width: '40%' }}
              options={[
                { label: 'PAID', value: 'PAID' },
                { label: 'UNPAID', value: 'UNPAID' },
              ]}
              disabled={installmentLoading}
            />
          </div>
      
          <div className="flex items-center justify-end gap-3">
            <Button onClick={() => setInstallmentModalVisible(false)} disabled={installmentLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleChangeInstallmentStatus}
              disabled={installmentLoading || !installmentValue}
              className="bg-blue-600"
            >
              {installmentLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Spin size="small" /> Changing...
                </span>
              ) : (
                'Change Installment Status'
              )}
            </Button>
          </div>
        </div>
      </Modal>
      

     
      {/* Student Status Modal */}
      <Modal title={<span className="text-lg font-bold">Change Student Status</span>}
        open={studentModalVisible}
        onCancel={() => setStudentModalVisible(false)}
        footer={null}
        centered
        width={520}
      >
        <div className="space-y-4">
          <div>Change student status for <strong>{studentRecord?.FullName ?? '-'}</strong></div>
          <div>
            <label className="block mb-1 text-sm text-gray-700">Email</label>
            <input readOnly value={studentRecord?.Email ?? ''} className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md" />
          </div>
          <div>
            <label className="block mb-1 text-sm text-gray-700">Student Status</label>
            <Select value={studentStatus} onChange={(v) => setStudentStatus(v)} style={{ width: '50%' }} options={[
              { label: 'ACTIVE', value: 'ACTIVE' }, { label: 'INACTIVE', value: 'INACTIVE' }, { label: 'SUSPENDED', value: 'SUSPENDED' }
            ]} />
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button onClick={() => setStudentModalVisible(false)} disabled={studentLoading}>Cancel</Button>
            <Button type="primary" onClick={handleUpdateStudentStatus} disabled={studentLoading || !studentStatus} className="bg-blue-600">
              {studentLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" />Updating...</span> : 'Update Status'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
  title={<span className="text-lg font-bold">Send Warning Message</span>}
  open={warningModalVisible}
  onCancel={() => {
    setWarningModalVisible(false);
    setWarningRecord(null);
  }}
  footer={null}
  centered
  width={600}
>
  <div className="space-y-4">
    <div>Sending Warning Message To : <strong>{warningRecord?.FullName ?? '-'}</strong></div>
    <div>
      <label className="block mb-1 text-gray-600 text-md">Email</label>
      <input
        type="text"
        value={warningRecord?.Email ?? ''}
        readOnly
        className="w-full px-3 py-2 text-sm font-semibold bg-gray-100 border border-gray-200 rounded-md"
      />
    </div>

    <div>
      <label className="block mb-1 text-gray-600 text-md">Phone Number</label>
      <input
        type="text"
        value={warningRecord?.PhoneNumber ?? ''}
        readOnly
        className="w-full px-3 py-2 text-sm font-semibold bg-gray-100 border border-gray-200 rounded-md"
      />
    </div>

    <div className="flex items-center justify-end gap-3">
      <Button onClick={() => setWarningModalVisible(false)} disabled={warningLoading}>Cancel</Button>

      <Button
        type="primary"
        onClick={() => handleSendWarning(warningRecord)}
        disabled={warningLoading}
        className="bg-red-600"
      >
        {warningLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" />Sending...</span> : 'Send Warning Message'}
      </Button>
    </div>
  </div>
</Modal>

<Modal
  title={<span className="text-lg font-bold">Update Second Installment Date</span>}
  visible={installmentDateModalVisible}
  onCancel={() => {
    setInstallmentDateModalVisible(false);
    setInstallmentDateRecord(null);
    setInstallmentDateValue(null);
  }}
  footer={null}
  centered
  width={520}
>
  <div className="space-y-4">
     <div className="text-sm text-gray-700">
      Update Second Installment Date to : <strong className="text-sm text-red-700">{installmentDateRecord?.FullName ?? '-'}</strong>
    </div>
    <div>
      <label className="block mb-1 text-gray-600 text-md">Email</label>
      <input
        type="text"
        value={installmentDateRecord?.Email ?? ''}
        readOnly
        className="w-full px-3 py-2 text-sm font-bold bg-gray-100 border border-gray-200 rounded-md"
      />
    </div>

    <div>
      <label className="block mb-1 text-gray-600 text-md">Second Installment Date</label>
      <DatePicker
        value={installmentDateValue}
        onChange={(d) => setInstallmentDateValue(d)}
        format="DD-MM-YYYY"
        disabledDate={(current) => {
          if (!current) return false;
          const today = dayjs().startOf('day');
          const min = dayjs().subtract(1, 'year').startOf('day'); // 1 year behind
          // disable if date is after today (future) or before min (older than 1 year)
          return current.isAfter(today, 'day') || current.isBefore(min, 'day');
        }}
        style={{ width: '40%' }}
      />
      <div className="mt-1 text-xs text-gray-500">
        Select a date within the past year up to today (format: DD-MM-YYYY)
      </div>
    </div>

    <div className="flex items-center justify-end gap-3">
      <Button onClick={() => setInstallmentDateModalVisible(false)} disabled={installmentDateLoading}>
        Cancel
      </Button>

      <Button
        type="primary"
        onClick={async () => {
          if (!installmentDateRecord || !installmentDateRecord.Email) {
            toast.error('No record selected');
            return;
          }
          if (!installmentDateValue || !installmentDateValue.isValid()) {
            toast.error('Select a valid date');
            return;
          }

          setInstallmentDateLoading(true);
          const keyName = `installmentDate-${installmentDateRecord.key}`;
          setInstallmentDateSendingKeys(prev => {
            const next = new Set(prev); next.add(keyName); return next;
          });

          const formatted = installmentDateValue.format('DD-MM-YYYY'); // e.g. 16-10-2025
          const payload = {
            Email: installmentDateRecord.Email,
            SecondInstallmentDate: formatted,
          };

          try {
            const url = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/pending-installment/update-installment-date`;
            const res = await axios.put(url, payload, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              timeout: 15000,
            });

            if (res?.data?.success || res?.status === 200 || res?.status === 201) {
              toast.success(res?.data?.message || 'Second installment date updated');

              // update UI
              setLeads(prev => prev.map(r => r.key === installmentDateRecord.key ? { ...r, SecondInstallmentDate: formatted } : r));

              setInstallmentDateModalVisible(false);
            } else {
              toast.error(res?.data?.message || 'Failed to update date');
            }
          } catch (err) {
            console.error('update-second-installment-date error', err);
            const msg = err?.response?.data?.message || err?.message || 'Network/server error';
            toast.error(msg);
          } finally {
            setInstallmentDateLoading(false);
            setInstallmentDateSendingKeys(prev => {
              const next = new Set(prev);
              next.delete(keyName);
              return next;
            });
          }
        }}
        disabled={installmentDateLoading}
        className="bg-blue-600"
      >
        {installmentDateLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" />Updating...</span> : 'Update Second Installment Date'}
      </Button>
    </div>
  </div>
</Modal>

<Modal
  title={<span className="text-lg font-bold">Update Second Installment Paid Amount</span>}
  visible={amountModalVisible}
  onCancel={() => {
    setAmountModalVisible(false);
    setAmountRecord(null);
    setAmountValue(null);
  }}
  footer={null}
  centered
  width={520}
>
  <div className="space-y-4">
    <div className="text-sm text-gray-700">
      Update Installment Paid Amount to : <strong className="text-sm text-red-700">{amountRecord?.FullName ?? '-'}</strong>
    </div>

    <div>
      <label className="block mb-1 text-gray-600 text-md">Email</label>
      <input
        type="text"
        value={amountRecord?.Email ?? ''}
        readOnly
        className="w-full px-3 py-2 text-sm font-bold bg-gray-100 border border-gray-200 rounded-md"
      />
    </div>

    <div>
      <label className="block mb-1 text-gray-600 text-md">Enrollment Type</label>
      <input
        type="text"
        value={amountRecord?.EnrollmentType ?? '-'}
        readOnly
        className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-md"
      />
    </div>

    <div>
      <label className="block mb-1 text-gray-600 text-md">Amount Paid</label>
      <div style={{ minWidth: '50%' }}>
        <InputNumber
          value={amountValue}
          onChange={(val) => {
            // InputNumber may pass '' or null for clear — normalize to null
            if (val === '' || val === undefined) setAmountValue(null);
            else setAmountValue(Number(val));
          }}
          placeholder="Enter amount (numbers only)"
          style={{ width: '50%' }}
          min={0}
          step={1}
          formatter={(v) => (v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
          parser={(v) => String(v).replace(/[, ]/g, '')}
        />
      </div>
    </div>

    <div className="flex items-center justify-end gap-3">
      <Button onClick={() => {
        setAmountModalVisible(false);
        setAmountRecord(null);
        setAmountValue(null);
      }} disabled={amountLoading}>Cancel</Button>

      <Button
        type="primary"
        onClick={() => handleAmountPaid(amountRecord)}
        disabled={amountLoading || amountValue === null || Number(amountValue) <= 0}
        className="bg-blue-600"
      >
        {amountLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" /> Updating...</span> : 'Update Paid Amount'}
      </Button>
    </div>
  </div>
</Modal>


<Modal
  title={<span className="text-lg font-bold">Update Pending Installment Comment</span>}
  open={commentModalVisible}
  onCancel={() => {
    setCommentModalVisible(false);
    setCommentRecord(null);
    setCommentValue("");
  }}
  footer={null}
  centered
  width={560}
>
  <div className="space-y-4">
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="mb-2 text-sm text-gray-800">
        Full Name :{" "}
        <strong className="text-red-800">
          {commentRecord?.FullName ?? "-"}
        </strong>
      </div>

      <div className="mb-2 text-sm text-gray-800">
        Email :{" "}
        <strong className="text-blue-800">
          {commentRecord?.Email ?? "-"}
        </strong>
      </div>

      <div className="text-sm text-gray-800">
        Phone Number :{" "}
        <strong className="text-green-800">
          {commentRecord?.PhoneNumber ?? "-"}
        </strong>
      </div>
    </div>

    <Input.TextArea
      rows={5}
      value={commentValue}
      onChange={(e) => setCommentValue(e.target.value)}
      placeholder="Enter pending installment comment..."
      disabled={commentLoading}
      maxLength={1000}
      showCount
    />

    <div className="flex items-center justify-end gap-3">
      <Button
        onClick={() => {
          setCommentModalVisible(false);
          setCommentRecord(null);
          setCommentValue("");
        }}
        disabled={commentLoading}
      >
        Cancel
      </Button>

      <Button
        type="primary"
        onClick={handleUpdateComment}
        disabled={commentLoading || !commentValue.trim()}
        className="bg-blue-600"
      >
        {commentLoading ? (
          <span className="inline-flex items-center gap-2">
            <Spin size="small" /> Updating...
          </span>
        ) : (
          "Update Comment"
        )}
      </Button>
    </div>
  </div>
</Modal>



    </div>
  );
}

export default PendingInstallments;
